import { useEffect, useRef, useState } from 'react';
import type { IconType } from 'react-icons';
import {
  LuCircle,
  LuCrosshair,
  LuEraser,
  LuEye,
  LuHand,
  LuHighlighter,
  LuMoveUpRight,
  LuPenLine,
  LuRedo2,
  LuSlash,
  LuSquare,
  LuTrash2,
  LuType,
  LuUndo2,
} from 'react-icons/lu';
import { POLYGONS } from '../../shared/geometry';
import { POLYGON_KINDS, type PolygonKind } from '../../shared/protocol';
import { IconButton } from '../components/ui';
import { useStore } from '../lib/store';
import { HIGHLIGHTS, PALETTE, SIZES, colorGroup, isPolygonTool, setTool, sizeGroup, toolStore, type Tool } from '../lib/tools';
import { MathMenu } from './MathMenu';

const TOOLS: { id: Tool; label: string; icon: IconType; teacherOnly?: boolean }[] = [
  { id: 'pan', label: 'Gezinme (V)', icon: LuHand },
  { id: 'pen', label: 'Kalem (P)', icon: LuPenLine },
  { id: 'highlighter', label: 'Fosforlu kalem (H)', icon: LuHighlighter },
  { id: 'eraser', label: 'Silgi (E)', icon: LuEraser },
  { id: 'line', label: 'Çizgi (L) — Shift: 15° adım', icon: LuSlash },
  { id: 'arrow', label: 'Ok (A)', icon: LuMoveUpRight },
  { id: 'rect', label: 'Dikdörtgen (R) — Shift: kare', icon: LuSquare },
  { id: 'ellipse', label: 'Elips (O) — Shift: daire', icon: LuCircle },
  { id: 'text', label: 'Metin (T)', icon: LuType },
  { id: 'laser', label: 'Lazer işaretçi (K)', icon: LuCrosshair, teacherOnly: true },
];

/** Simge, çizimde kullanılan köşe verisinden üretilir; menü ile tuvaldeki şekil hep aynıdır. */
function PolygonIcon({ kind }: { kind: PolygonKind }) {
  const points = POLYGONS[kind].vertices.map(([u, v]) => `${2 + u * 16},${2 + v * 16}`).join(' ');
  return (
    <svg viewBox="0 0 20 20" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
      <polygon points={points} />
    </svg>
  );
}

/** Araç çubuğu kaydırılabildiği için menü, kesilmesin diye ekrana sabitlenir. */
function PolygonMenu({ disabled }: { disabled: boolean }) {
  const tool = useStore(toolStore, (s) => s.tool);
  const last = useStore(toolStore, (s) => s.polygon);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!pos) return;
    const close = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setPos(null);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPos(null);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', key);
    };
  }, [pos]);

  const toggle = () => {
    if (pos) return setPos(null);
    const r = button.current!.getBoundingClientRect();
    const height = 8 + POLYGON_KINDS.length * 40;
    setPos({ left: r.right + 8, top: Math.max(8, Math.min(r.top, window.innerHeight - height - 8)) });
  };

  return (
    <div ref={root}>
      <button
        ref={button}
        type="button"
        className={`icon-btn ${isPolygonTool(tool) ? 'is-active' : ''}`}
        title="Geometrik şekiller (G) — Shift: düzgün şekil"
        aria-label="Geometrik şekiller (G)"
        aria-haspopup="menu"
        aria-expanded={pos !== null}
        disabled={disabled}
        onClick={toggle}
      >
        <PolygonIcon kind={last} />
      </button>
      {pos && (
        <div className="shape-menu" role="menu" style={pos}>
          {POLYGON_KINDS.map((kind) => (
            <button
              key={kind}
              type="button"
              role="menuitemradio"
              aria-checked={tool === kind}
              className={tool === kind ? 'is-active' : ''}
              onClick={() => {
                setTool(kind);
                setPos(null);
              }}
            >
              <PolygonIcon kind={kind} />
              {POLYGONS[kind].label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export interface ToolbarProps {
  isTeacher: boolean;
  permitted: boolean;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onClearPage: () => void;
}

export function Toolbar(p: ToolbarProps) {
  const tool = useStore(toolStore, (s) => s.tool);
  const colors = useStore(toolStore, (s) => s.color);
  const sizes = useStore(toolStore, (s) => s.size);
  const cg = colorGroup(tool);
  const sg = sizeGroup(tool);
  const palette = cg === 'highlighter' ? HIGHLIGHTS : PALETTE;
  const enabled = (t: Tool) => t === 'pan' || (t === 'laser' ? p.isTeacher : p.permitted);

  return (
    <aside className="toolbar" aria-label="Araçlar">
      <div className="tool-group">
        {TOOLS.filter((t) => !t.teacherOnly || p.isTeacher).map((t) => (
          <IconButton key={t.id} label={t.label} active={tool === t.id} disabled={!enabled(t.id)} onClick={() => setTool(t.id)}>
            <t.icon />
          </IconButton>
        ))}
        <PolygonMenu disabled={!p.permitted} />
        <MathMenu disabled={!p.permitted} />
      </div>

      {(p.permitted || tool === 'laser') && cg && (
        <div className="tool-group swatches" role="radiogroup" aria-label="Renk">
          {palette.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={colors[cg] === c}
              aria-label={`Renk ${c}`}
              className={`swatch ${colors[cg] === c ? 'is-active' : ''}`}
              style={{ background: c }}
              onClick={() => toolStore.set((s) => ({ color: { ...s.color, [cg]: c } }))}
            />
          ))}
        </div>
      )}

      {p.permitted && sg && (
        <div className="tool-group sizes" role="radiogroup" aria-label="Kalınlık">
          {([0, 1, 2] as const).map((i) => (
            <button
              key={i}
              type="button"
              role="radio"
              aria-checked={sizes[sg] === i}
              aria-label={['İnce', 'Orta', 'Kalın'][i]}
              title={sg === 'text' ? `${SIZES.text[i]} pt` : ['İnce', 'Orta', 'Kalın'][i]}
              className={`size-btn ${sizes[sg] === i ? 'is-active' : ''}`}
              onClick={() => toolStore.set((s) => ({ size: { ...s.size, [sg]: i } }))}
            >
              {sg === 'text' ? <span className="size-a" style={{ fontSize: 10 + i * 4 }}>A</span> : <span className="size-dot" style={{ width: 4 + i * 4, height: 4 + i * 4 }} />}
            </button>
          ))}
        </div>
      )}

      <div className="tool-group">
        <IconButton label="Geri al (Ctrl+Z)" onClick={p.onUndo} disabled={!p.canUndo || !p.permitted}>
          <LuUndo2 />
        </IconButton>
        <IconButton label="Yinele (Ctrl+Shift+Z)" onClick={p.onRedo} disabled={!p.canRedo || !p.permitted}>
          <LuRedo2 />
        </IconButton>
        {p.isTeacher && (
          <IconButton label="Bu sayfadaki tüm çizimleri sil" onClick={p.onClearPage} danger>
            <LuTrash2 />
          </IconButton>
        )}
      </div>

      {!p.permitted && (
        <div className="toolbar-note" title="Öğretmen çizim izni verene kadar yalnızca izlersiniz">
          <LuEye />
          <span>İzleme</span>
        </div>
      )}
    </aside>
  );
}
